import React, { FC, useCallback, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { ULContainer } from '../../Common/Layout/ULContainer.styled';
import Navbar from '../../Common/Navbar/Navbar';
import {
    SignUpContainer,
    SignUpLayout,
    SignUpTitle,
} from '../../Common/UniversalSignup/UniversalSignup.styled';
import { Box, Button, GridV2, Label } from '@kite-js/core';
import { CmsIds } from '../../../constants/common/CmsIds';
import { setSignupPartnerTypeCookie } from '../../../utils/signupPartnerTypeCookie';
import { CheckBoxMargin, SpanMargin } from '../../Common/CheckBoxMargin/CheckBoxMargin.styled';
import { Select as SelectBeta } from '@kite-js/experimental';
import { usePartnerTypeSelection } from '../../../hooks/usePartnerTypeSelection';

interface FormErrors {
    partnerType: string | null;
    agreement: string | null;
}

interface SignUpProps {
    locale?: string;
    cmsItems: Cms.CmsDictionary;
}

const SignUpPartnerType: FC<SignUpProps> = ({ cmsItems, locale: _locale }) => {
    const params = useParams<{ culture?: string }>();
    const location = useLocation();
    const [agreementAccepted, setAgreementAccepted] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [formError, setFormError] = useState<FormErrors>({
        partnerType: null,
        agreement: null,
    });

    const culturePath = params.culture ? `/${params.culture}` : '';

    const handlePartnerTypeChange = useCallback(() => {
        setAgreementAccepted(false);
    }, []);

    const {
        partnerType,
        partnerTypeItems,
        partnerTypeSelectValue,
        handlePartnerTypeSelectChange: hookHandlePartnerTypeSelectChange,
        agreementLabelContent,
    } = usePartnerTypeSelection(cmsItems, {
        onPartnerTypeChange: handlePartnerTypeChange,
    });

    const handlePartnerTypeSelectChange = useCallback(
        (selected: Parameters<typeof hookHandlePartnerTypeSelectChange>[0]) => {
            hookHandlePartnerTypeSelectChange(selected);
            setFormError((prev) =>
                prev.partnerType === null ? prev : { ...prev, partnerType: null },
            );
        },
        [hookHandlePartnerTypeSelectChange],
    );

    const handleAgreementChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        setAgreementAccepted(checked);
        if (checked) {
            setFormError((prev) =>
                prev.agreement === null ? prev : { ...prev, agreement: null },
            );
        }
    }, []);

    const agreementLabel = <SpanMargin>{agreementLabelContent}</SpanMargin>;

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setSubmitAttempted(true);

        const nextErrors: FormErrors = {
            partnerType: null,
            agreement: null,
        };

        if (partnerType === null) {
            nextErrors.partnerType = cmsItems[CmsIds.PartnerTypeRequired];
        }
        if (!agreementAccepted) {
            nextErrors.agreement = cmsItems[CmsIds.PleaseAcknowledge];
        }

        setFormError(nextErrors);

        if (partnerType === null || !agreementAccepted) {
            return;
        }

        setSignupPartnerTypeCookie(partnerType);
        window.location.href = `${culturePath}/signup${location.search}`;
    };

    const selectTouched = submitAttempted || !!formError.partnerType;
    const agreementTouched = submitAttempted || agreementAccepted || !!formError.agreement;
    const partnerTypeSelectVariant =
        selectTouched && formError.partnerType ? 'error' : undefined;
    const partnerTypeHelperText =
        selectTouched && formError.partnerType ? formError.partnerType : undefined;

    return (
        <ULContainer>
            <Navbar cmsItems={cmsItems} />
            <SignUpLayout>
                <SignUpContainer>
                    <SignUpTitle style={{ fontWeight: 700, marginTop: 0 }}>
                        {cmsItems[CmsIds.SignupTypePageTitle]}
                    </SignUpTitle>
                    <form
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            padding: '8px 24px 0',
                        }}
                        onSubmit={handleSubmit}
                    >
                        <GridV2 direction="column" spacing="XXXL" width="100%">
                            <GridV2.Item sm={12} style={{ padding: '8px 0', textAlign: 'left' }}>
                                <Box mb="XS" py='XS'>
                                    <Label>{cmsItems[CmsIds.PartnerTypeFieldLabel]}</Label>
                                </Box>
                                <SelectBeta
                                    title={partnerTypeSelectValue?.label ?? ''}
                                    items={partnerTypeItems}
                                    multiSelect={false}
                                    allowSearch={false}
                                    value={partnerTypeSelectValue}
                                    onChange={handlePartnerTypeSelectChange}
                                    variant={partnerTypeSelectVariant}
                                    helperText={partnerTypeHelperText}
                                    data-testid="partner-type-select"
                                />
                            </GridV2.Item>
                            <GridV2.Item sm={12} style={{ padding: '8px 0' }}>
                                <CheckBoxMargin
                                    name="partnerAgreement"
                                    checked={agreementAccepted}
                                    onChange={handleAgreementChange}
                                    errors={formError.agreement ?? undefined}
                                    touched={agreementTouched}
                                    inputAlignment="top"
                                    disabled={partnerType === null}
                                    data-testid="partner-agreement"
                                >
                                    {agreementLabel}
                                </CheckBoxMargin>
                            </GridV2.Item>
                            <GridV2.Item sm={12}>
                                <Box pt="S" pb="M">
                                    <Button
                                        type="submit"
                                        fullWidth
                                        data-testid="partner-type-submit"
                                    >
                                        {cmsItems[CmsIds.CreateAccount]}
                                    </Button>
                                </Box>
                            </GridV2.Item>
                        </GridV2>
                    </form>
                </SignUpContainer>
            </SignUpLayout>
        </ULContainer>
    );
};

export default SignUpPartnerType;
